const normalize = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const normalizeEmail = (value) => String(value || "").trim().toLowerCase();
const CARRIER_MANAGEMENT_URL = "https://management.crediarc.net/";

const asTime = (value) => {
  const time = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(time) ? time : 0;
};

const titleCase = (value) => String(value || "")
  .replaceAll("_", " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const uniqueBy = (rows, keyFor) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = keyFor(row);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export function getCarrierUserViewUrl(carrierInsights, email) {
  if (!carrierInsights || !email) return null;
  const targetEmail = normalizeEmail(email);
  const profile = (carrierInsights.users || []).find(
    (user) => normalizeEmail(user.email) === targetEmail
  );
  if (!profile?.user_id) return null;
  return `${CARRIER_MANAGEMENT_URL}platform-console/view-as/${encodeURIComponent(profile.user_id)}`;
}

export function matchCarrierCompanies(tenant, directory = {}) {
  const companies = Array.isArray(directory.companies) ? directory.companies : [];
  const configuredId = tenant?.metadata?.carrierPlatformCompanyId || tenant?.carrierPlatformCompanyId;
  const tenantName = normalize(tenant?.name);
  const tenantEmails = new Set([
    tenant?.email,
    ...(Array.isArray(tenant?.emailList) ? tenant.emailList : []),
  ].map(normalizeEmail).filter(Boolean));
  const emailCompanyIds = new Set((directory.users || [])
    .filter((user) => tenantEmails.has(normalizeEmail(user.email)))
    .map((user) => user.company_id)
    .filter(Boolean));

  const configured = companies.find((company) => company.id === configuredId);
  const exact = companies.find((company) => tenantName && normalize(company.name) === tenantName);
  const emailMatch = companies.find((company) => emailCompanyIds.has(company.id));
  const anchor = configured || exact || emailMatch;
  if (!anchor) {
    if (!tenantName) return [];
    const partial = companies.find((company) => {
      const companyName = normalize(company.name);
      return companyName && (companyName.includes(tenantName) || tenantName.includes(companyName));
    });
    if (!partial) return [];
  }

  const anchorName = normalize((anchor || companies.find((company) => {
    const companyName = normalize(company.name);
    return companyName && (companyName.includes(tenantName) || tenantName.includes(companyName));
  }))?.name);
  return companies.filter((company) => {
    if (company.id === configuredId || emailCompanyIds.has(company.id)) return true;
    const companyName = normalize(company.name);
    return Boolean(companyName && (
      (tenantName && (companyName === tenantName || companyName.includes(tenantName) || tenantName.includes(companyName)))
      || (anchorName && (companyName === anchorName || companyName.includes(anchorName) || anchorName.includes(companyName)))
    ));
  });
}

export function matchCarrierCompany(tenant, directory = {}) {
  return matchCarrierCompanies(tenant, directory)[0] || null;
}

export function buildCarrierInsights(tenant, directory = {}, now = Date.now()) {
  const companies = matchCarrierCompanies(tenant, directory);
  if (!companies.length) return null;

  const companyIds = new Set(companies.map((company) => company.id));
  const users = uniqueBy(
    (directory.users || []).filter((user) => companyIds.has(user.company_id)),
    (user) => user.user_id || normalizeEmail(user.email),
  );
  const invitations = uniqueBy(
    (directory.invitations || []).filter((invite) => companyIds.has(invite.company_id)),
    (invite) => invite.id,
  );
  const submissions = uniqueBy(
    (directory.submissions || []).filter((submission) => companyIds.has(submission.company_id)),
    (submission) => submission.id,
  );
  const userIds = new Set(users.map((user) => user.user_id));
  const loginEvents = (directory.loginEvents || []).filter((event) => userIds.has(event.user_id));
  const invitationIds = new Set(invitations.map((invite) => invite.id));
  const linkEvents = (directory.linkEvents || []).filter((event) => invitationIds.has(event.invite_id));
  const onboarding = [...submissions].sort((left, right) =>
    (Number(right.completion_percent) || 0) - (Number(left.completion_percent) || 0)
    || (Number(right.current_section) || 0) - (Number(left.current_section) || 0)
    || asTime(right.updated_at) - asTime(left.updated_at)
  )[0]
    || companies.map((candidate) => candidate.onboarding).filter(Boolean).sort((left, right) =>
      (Number(right.completion_percent) || 0) - (Number(left.completion_percent) || 0)
      || asTime(right.updated_at) - asTime(left.updated_at)
    )[0]
    || null;
  const company = companies.find((candidate) => candidate.id === onboarding?.company_id)
    || companies.find((candidate) => candidate.id === (tenant?.metadata?.carrierPlatformCompanyId || tenant?.carrierPlatformCompanyId))
    || companies[0];
  const latestInvite = [...invitations].sort((left, right) => asTime(right.updated_at || right.invited_at) - asTime(left.updated_at || left.invited_at))[0];
  const active = users.some((user) => user.account_stage === "active") || invitations.some((invite) => invite.status === "activated");
  const premium = users.some((user) => user.is_premium);
  const status = active ? "Active"
    : onboarding?.status === "submitted" || onboarding?.status === "in_review" ? "Awaiting activation"
      : onboarding ? "Onboarding"
        : latestInvite ? titleCase(latestInvite.status) : "Not started";
  const thirtyDaysAgo = now - (30 * 24 * 60 * 60 * 1000);
  const recentLogins = loginEvents.filter((event) => asTime(event.login_at) >= thirtyDaysAgo);
  const activeUsers30d = new Set(recentLogins.map((event) => event.user_id)).size;

  const journey = [];
  const push = (timestamp, label, detail, kind) => {
    if (asTime(timestamp)) journey.push({ timestamp, label, detail, kind });
  };
  invitations.forEach((invite) => {
    const person = invite.full_name || invite.email || "Carrier user";
    push(invite.invited_at || invite.created_at, "Onboarding invitation created", `${person} · ${titleCase(invite.activation_role)}`, "invitation");
    push(invite.link_sent_at, "Onboarding link sent", invite.email, "invitation");
    push(invite.activated_at, "Carrier access activated", `${person} · ${titleCase(invite.activation_role)}`, "activation");
  });
  linkEvents.forEach((event) => {
    push(event.first_opened_at, event.link_kind === "activation" ? "Activation link opened" : "Onboarding link opened", `${Number(event.open_count) || 1} open${Number(event.open_count) === 1 ? "" : "s"}`, "invitation");
  });
  submissions.forEach((submission) => {
    const person = submission.contact_email || users.find((user) => user.user_id === submission.user_id)?.email || "Carrier user";
    push(submission.created_at, "Surety onboarding started", person, "onboarding");
    if (submission.submitted_at) {
      push(submission.submitted_at, "Surety onboarding submitted", `${person} · 100% complete`, "onboarding");
    } else {
      push(submission.updated_at, "Onboarding progress saved", `${person} · ${Number(submission.completion_percent) || 0}% complete`, "onboarding");
    }
  });
  loginEvents.forEach((event) => push(event.login_at, "Carrier platform sign-in", event.email || "Carrier user", "login"));
  journey.sort((left, right) => asTime(right.timestamp) - asTime(left.timestamp));

  return {
    company,
    companies,
    users,
    invitations,
    submissions,
    loginEvents,
    linkEvents,
    onboarding,
    plan: premium ? "Premium Carrier & Surety" : "Standard Carrier & Surety",
    status,
    completionPercent: Number(onboarding?.completion_percent) || 0,
    onboardingStatus: titleCase(onboarding?.status || latestInvite?.status || "not started"),
    logins30d: recentLogins.length,
    activeUsers30d,
    lastActivity: journey[0]?.timestamp || null,
    journey,
  };
}

export function buildCarrierUserJourney(carrierInsights, email) {
  if (!carrierInsights || !email) return null;
  const targetEmail = normalizeEmail(email);
  const profile = carrierInsights.users.find((user) => normalizeEmail(user.email) === targetEmail) || null;
  const invitations = carrierInsights.invitations.filter((invite) =>
    (profile?.user_id && invite.user_id === profile.user_id) || normalizeEmail(invite.email) === targetEmail
  );
  const invitationIds = new Set(invitations.map((invite) => invite.id));
  const submissions = (carrierInsights.submissions || []).filter((submission) =>
    (profile?.user_id && submission.user_id === profile.user_id) || normalizeEmail(submission.contact_email) === targetEmail
  );
  const loginEvents = (carrierInsights.loginEvents || []).filter((event) =>
    (profile?.user_id && event.user_id === profile.user_id) || normalizeEmail(event.email) === targetEmail
  );
  const linkEvents = (carrierInsights.linkEvents || []).filter((event) => invitationIds.has(event.invite_id));
  if (!profile && !invitations.length && !submissions.length && !loginEvents.length) return null;

  const latestInvite = [...invitations].sort((left, right) => asTime(right.updated_at || right.invited_at) - asTime(left.updated_at || left.invited_at))[0] || null;
  const latestSubmission = [...submissions].sort((left, right) => asTime(right.updated_at) - asTime(left.updated_at))[0] || null;
  const onboardingOpens = linkEvents.filter((event) => event.link_kind !== "activation" && event.first_opened_at);
  const activationOpens = linkEvents.filter((event) => event.link_kind === "activation" && event.first_opened_at);
  const firstOnboardingOpen = [...onboardingOpens].sort((left, right) => asTime(left.first_opened_at) - asTime(right.first_opened_at))[0];
  const latestLogin = [...loginEvents].sort((left, right) => asTime(right.login_at) - asTime(left.login_at))[0];
  const submittedAt = latestSubmission?.submitted_at
    || (["submitted", "in_review", "completed"].includes(latestSubmission?.status) ? latestSubmission?.updated_at : null)
    || (["submitted", "activated"].includes(latestInvite?.status) ? latestInvite?.updated_at : null);
  const activatedAt = latestInvite?.activated_at || (profile?.account_stage === "active" ? profile?.updated_at : null);
  const downstreamOfOpen = latestSubmission?.created_at || submittedAt || activatedAt || latestLogin?.login_at;
  const openTimestamp = firstOnboardingOpen?.first_opened_at || downstreamOfOpen || null;

  const stages = [
    { key: "invited", label: "Invited", timestamp: latestInvite?.invited_at || latestInvite?.created_at, complete: Boolean(latestInvite) },
    { key: "sent", label: "Link sent", timestamp: latestInvite?.link_sent_at, complete: Boolean(latestInvite?.link_sent_at) },
    { key: "opened", label: "Link opened", timestamp: openTimestamp, complete: Boolean(openTimestamp), inferred: !firstOnboardingOpen && Boolean(downstreamOfOpen) },
    { key: "started", label: "Onboarding started", timestamp: latestSubmission?.created_at, complete: Boolean(latestSubmission) },
    { key: "submitted", label: "Submitted", timestamp: submittedAt, complete: Boolean(submittedAt) },
    { key: "activated", label: "Activated", timestamp: activatedAt, complete: Boolean(activatedAt) },
    { key: "signed_in", label: "Signed in", timestamp: latestLogin?.login_at, complete: Boolean(latestLogin) },
  ];
  const completedStageIndexes = stages.map((stage, index) => stage.complete ? index : -1).filter((index) => index >= 0);
  const currentStageIndex = completedStageIndexes.length ? Math.max(...completedStageIndexes) : -1;
  stages.forEach((stage, index) => {
    stage.state = stage.complete ? (index === currentStageIndex ? "current" : "complete") : "pending";
  });

  const events = [];
  const sessionId = `carrier-surety-${profile?.user_id || targetEmail}`;
  const push = (timestamp, event, props = {}) => {
    const ts = asTime(timestamp);
    if (ts) events.push({ event, ts, clientTs: ts, sessionId, props: { ...props, product: "carrier_surety" } });
  };
  invitations.forEach((invite) => {
    push(invite.invited_at || invite.created_at, "carrier_invited", { role: invite.activation_role });
    push(invite.link_sent_at, "carrier_link_sent", { role: invite.activation_role });
    push(invite.activated_at, "carrier_activated", { role: invite.activation_role });
  });
  linkEvents.forEach((event) => {
    push(event.first_opened_at, event.link_kind === "activation" ? "carrier_activation_link_opened" : "carrier_onboarding_link_opened", { openCount: Number(event.open_count) || 1 });
  });
  submissions.forEach((submission) => {
    push(submission.created_at, "carrier_onboarding_started", { completionPercent: submission.completion_percent, section: submission.current_section });
    if (submission.submitted_at || ["submitted", "in_review", "completed"].includes(submission.status)) {
      push(submission.submitted_at || submission.updated_at, "carrier_onboarding_submitted", { completionPercent: submission.completion_percent });
    } else if (submission.updated_at && submission.updated_at !== submission.created_at) {
      push(submission.updated_at, "carrier_onboarding_progress", { completionPercent: submission.completion_percent, section: submission.current_section });
    }
  });
  loginEvents.forEach((event) => push(event.login_at, "carrier_signed_in"));
  events.sort((left, right) => eventTimeForSort(left) - eventTimeForSort(right));

  return {
    product: "Carrier & Surety",
    company: carrierInsights.company,
    profile,
    currentStage: stages[currentStageIndex]?.label || "Not started",
    stages,
    events,
    onboarding: latestSubmission,
    openCount: linkEvents.reduce((sum, event) => sum + (Number(event.open_count) || 0), 0),
    activationLinkOpenedAt: activationOpens.sort((left, right) => asTime(right.first_opened_at) - asTime(left.first_opened_at))[0]?.first_opened_at || null,
  };
}

const eventTimeForSort = (event) => Number(event?.clientTs) || Number(event?.ts) || 0;
